// Native `<select>` driven by `z.enum([...])` values per
// spec/tech-arch.md §7. Provides keyboard navigation, accessibility, and
// platform-correct focus styles for free; no third-party combobox at V1.
//
// **Placeholder option.** A leading placeholder `<option value="">` is
// always rendered first. The two arms differ by intent:
// - **`required === true`** — placeholder reads "Select…" and is
//   `disabled` so the user cannot submit the empty selection.
// - **`required === false` (optional enum)** — placeholder reads
//   "(none)" and is selectable, so the user can revert to a blank
//   value after picking one (P-037.22 / D-130). Empty string is a
//   valid "no selection" state for an optional enum.
//
// **Raw enum values as option labels.** V1 renders the raw Zod enum
// value as the option text (e.g., `'newsletter'`). Label improvement
// deferred to P-035 polish.

import { FieldShell } from './field-shell'

interface SelectFieldProps {
  readonly id: string
  readonly name: string
  readonly label: string
  readonly helpText?: string
  readonly value: string
  readonly onChange: (next: string) => void
  readonly onBlur?: () => void
  readonly error?: string
  readonly required?: boolean
  readonly options: readonly string[]
}

export function SelectField({
  id,
  name,
  label,
  helpText,
  value,
  onChange,
  onBlur,
  error,
  required = false,
  options,
}: SelectFieldProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      {...(helpText !== undefined ? { helpText } : {})}
      {...(error !== undefined ? { error } : {})}
    >
      {(slot) => (
        <select
          {...slot.attrs}
          name={name}
          value={value}
          onChange={(e) => onChange((e.currentTarget as HTMLSelectElement).value)}
          onBlur={onBlur}
          class={slot.inputClass}
        >
          {required ? (
            <option value="" disabled>
              Select…
            </option>
          ) : (
            <option value="">(none)</option>
          )}
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  )
}
